import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Mail } from "lucide-react";
import { MaskReveal, Reveal } from "./Motion";
import { BRAND, PROJECT_TYPES, BUDGET_RANGES } from "../data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", project_type: "", budget: "", message: "", website: "" };

const inputCls =
  "w-full bg-transparent border border-white/15 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:border-white/50 focus:outline-none transition-colors duration-300";

// Form state + submission against POST /api/inquiries.
function useInquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          project_type: form.project_type || null,
          budget: form.budget || null,
          website: form.website || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Something went wrong.");
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      setStatus("error");
      setError(err.message || "Something went wrong. Please email me directly.");
    }
  };

  return { form, set, submit, status, setStatus, error };
}

function SuccessCard({ onReset }) {
  return (
    <div
      data-testid="contact-success"
      className="rounded-2xl border border-white/15 bg-[#0C0C0C] p-10 flex flex-col items-start gap-4"
    >
      <span className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center">
        <Check size={22} />
      </span>
      <h3 className="font-display font-bold text-2xl tracking-tight">Message sent</h3>
      <p className="text-white/55 max-w-sm">
        Thanks, your inquiry landed in my inbox. I&apos;ll get back to you within 24 hours.
      </p>
      <button onClick={onReset} data-testid="contact-reset" className="mt-2 text-sm text-white/60 hover:text-white link-underline">
        Send another
      </button>
    </div>
  );
}

function SelectField({ value, onChange, placeholder, options, testId }) {
  return (
    <select
      value={value}
      onChange={onChange}
      data-testid={testId}
      className={`${inputCls} ${value ? "text-white" : "text-white/30"}`}
    >
      <option value="" className="bg-[#0C0C0C]">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0C0C0C] text-white">{o}</option>
      ))}
    </select>
  );
}

function InquiryForm({ form, set, submit, status, error }) {
  return (
    <form onSubmit={submit} data-testid="contact-form" className="flex flex-col gap-4">
      <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor="inquiry-website">Website</label>
        <input
          id="inquiry-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
          data-testid="contact-honeypot"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input
          required
          type="text"
          placeholder="Your name"
          value={form.name}
          onChange={set("name")}
          data-testid="contact-name"
          className={inputCls}
        />
        <input
          required
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={set("email")}
          data-testid="contact-email"
          className={inputCls}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <SelectField
          value={form.project_type}
          onChange={set("project_type")}
          placeholder="Project type"
          options={PROJECT_TYPES}
          testId="contact-project-type"
        />
        <SelectField
          value={form.budget}
          onChange={set("budget")}
          placeholder="Budget range"
          options={BUDGET_RANGES}
          testId="contact-budget"
        />
      </div>

      <textarea
        required
        rows={5}
        placeholder="Tell me about the keynote or site, your timeline and audience…"
        value={form.message}
        onChange={set("message")}
        data-testid="contact-message"
        className={`${inputCls} resize-none`}
      />

      {status === "error" && (
        <p data-testid="contact-error" className="text-sm text-red-400">{error}</p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        data-testid="contact-submit"
        className="group inline-flex items-center justify-center gap-3 rounded-full bg-white text-black px-8 py-4 text-base font-medium hover:bg-white/85 transition-colors disabled:opacity-60"
      >
        {status === "submitting" ? (
          <>Sending<Loader2 size={18} className="animate-spin" /></>
        ) : (
          <>Send inquiry<ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" /></>
        )}
      </button>
    </form>
  );
}

function ContactHeadline() {
  return (
    <div>
      <h2 className="font-display font-extrabold tracking-tighter leading-[0.9] text-[9vw] md:text-[5.5vw] lg:text-[4.4vw]">
        <MaskReveal lines={["Got a launch,"]} />
        <span className="block">
          <MaskReveal delay={0.1} lines={["a keynote or a"]} />
        </span>
        <span className="block">
          <span className="reveal-line">
            <span className="block">
              site <span className="font-serif-accent italic font-normal text-white/85">coming up?</span>
            </span>
          </span>
        </span>
      </h2>
      <Reveal delay={0.1} className="mt-10">
        <p className="text-white/55 max-w-md leading-relaxed">
          Tell me about it and I&apos;ll reply within 24 hours with next steps. Prefer email? Reach me directly below.
          You can also browse the{" "}
          <Link to="/#work" data-testid="contact-link-work" className="link-underline text-white/80 hover:text-white">selected work</Link>{" "}
          or my{" "}
          <a href={BRAND.behance} target="_blank" rel="noopener noreferrer" data-testid="contact-behance-link" className="link-underline text-white/80 hover:text-white">
            Behance portfolio
          </a>.
        </p>
        <a
          href={`mailto:${BRAND.email}`}
          data-testid="contact-email-btn"
          className="lift mt-8 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/20 backdrop-blur-md px-7 py-3.5 text-sm text-white/85 hover:bg-white hover:text-black transition-colors duration-300"
        >
          Email me <Mail size={16} />
        </a>
      </Reveal>
    </div>
  );
}

export default function Contact() {
  const { form, set, submit, status, setStatus, error } = useInquiryForm();

  return (
    <section id="contact" className="relative py-28 md:py-44 scroll-mt-20" data-testid="contact">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Start a project</p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <ContactHeadline />
          <Reveal delay={0.15}>
            {status === "success" ? (
              <SuccessCard onReset={() => setStatus("idle")} />
            ) : (
              <InquiryForm form={form} set={set} submit={submit} status={status} error={error} />
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
